"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from "react";
import { UserEvents, trackEvent } from "@/analytics";
import {
  createFeatureAnnouncementUserVisit,
  getFeatureAnnouncementBySlug,
  getFeatureAnnouncementVisitsForUser,
} from "@/data/v2/client/feature-announcements";
import { getCDNImageURL } from "@/data/images";
import { logException } from "@/analytics/sentry";

const LOCALSTORAGE_KEY = "feature_announcement_visits";

interface FeatureAnnouncementAction {
  textLabel: string;
  action: () => void;
}
export interface FeatureAnnouncementContent {
  imageSrc: string;
  title: string;
  body: string;
  dismissCTA: FeatureAnnouncementAction;
  actionCTA: FeatureAnnouncementAction;
}

interface FeatureAnnouncementContextType {
  isOpen: boolean;
  content: FeatureAnnouncementContent | null;
  /**
   * Show feature announcement modal by DB slug
   */
  showFeatureAnnouncementModal: (
    slug: string,
    dismissCTA: FeatureAnnouncementAction,
    actionCTA: FeatureAnnouncementAction
  ) => Promise<void>;
  /**
   * Close the currently open modal. If there is another modal in the queue, it will be displayed next.
   */
  close: () => void;
}

const FeatureAnnouncementContext = createContext<
  FeatureAnnouncementContextType | undefined
>(undefined);

interface Props {
  children: ReactNode;
}

export const FeatureAnnouncementProvider = ({ children }: Props) => {
  const [queue, setQueue] = useState<FeatureAnnouncementContent[]>([]);

  const isOpen = queue.length > 0;
  const content = isOpen ? queue[0] : null;

  const show = useCallback((modalContent: FeatureAnnouncementContent) => {
    setQueue((prev) => {
      // Avoid enqueuing duplicate identical content consecutively
      if (
        prev.length > 0 &&
        JSON.stringify(prev[prev.length - 1]) === JSON.stringify(modalContent)
      ) {
        return prev;
      }
      return [...prev, modalContent];
    });
  }, []);

  /**
   * Check if user has visited this announcement (from localStorage)
   */
  const hasVisitedLocally = useCallback((announcementId: string): boolean => {
    try {
      const visits = localStorage.getItem(LOCALSTORAGE_KEY);
      if (!visits) return false;
      const visitedIds = JSON.parse(visits) as string[];
      return visitedIds.includes(announcementId);
    } catch (err) {
      logException(err);
      return false;
    }
  }, []);

  /**
   * Mark announcement as visited in localStorage
   */
  const markAsVisitedLocally = useCallback((announcementId: string) => {
    try {
      const visits = localStorage.getItem(LOCALSTORAGE_KEY);
      const visitedIds = visits ? (JSON.parse(visits) as string[]) : [];
      if (!visitedIds.includes(announcementId)) {
        visitedIds.push(announcementId);
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(visitedIds));
      }
    } catch (err) {
      logException(err);
    }
  }, []);

  /**
   * Mark announcement as visited (both in DB and localStorage)
   */
  const markAsVisited = useCallback(
    async (announcementId: string) => {
      try {
        // Save to localStorage for non-logged in users
        markAsVisitedLocally(announcementId);
        // Also try to save to DB (will gracefully handle if user is not logged in)
        await createFeatureAnnouncementUserVisit(announcementId);
      } catch (err) {
        logException(err);
      }
    },
    [markAsVisitedLocally]
  );

  const showFeatureAnnouncementModal = useCallback(
    async (
      slug: string,
      dismissCTA: FeatureAnnouncementAction,
      actionCTA: FeatureAnnouncementAction
    ) => {
      try {
        const announcement = await getFeatureAnnouncementBySlug(slug);

        // Check if user has already visited this announcement
        if (announcement?.id) {
          // Check localStorage first (for non-logged in users)
          if (hasVisitedLocally(announcement.id)) {
            return; // Don't show modal if already visited locally
          }

          // Also check database (for logged in users)
          const userVisits = await getFeatureAnnouncementVisitsForUser();
          const hasVisitedInDB = userVisits.some(
            (visit) => visit.feature_announcement_id === announcement.id
          );

          if (hasVisitedInDB) {
            // Sync to localStorage so we don't check DB again
            markAsVisitedLocally(announcement.id);
            return; // Don't show modal if already visited
          }
        }

        if (announcement) {
          const content: FeatureAnnouncementContent = {
            imageSrc:
              getCDNImageURL(announcement.logo_id ?? "") ||
              "/resources/logo.png", //fallback image
            title: announcement.title || "",
            body: announcement.body || "",
            dismissCTA: {
              textLabel: dismissCTA.textLabel,
              action: async () => {
                // Mark as visited first
                if (announcement.id) {
                  await markAsVisited(announcement.id);
                }
                trackEvent(UserEvents.FEATURE_ANNOUNCEMENT_MODAL_DISMISSED, {
                  feature: announcement.slug,
                });
                // Then execute the user's dismiss action
                dismissCTA.action();
              },
            },
            actionCTA: {
              textLabel: actionCTA.textLabel,
              action: async () => {
                // Mark as visited first
                if (announcement.id) {
                  await markAsVisited(announcement.id);
                }
                trackEvent(UserEvents.FEATURE_ANNOUNCEMENT_MODAL_ACTION_TAKEN, {
                  feature: announcement.slug,
                });
                // Then execute the user's action
                actionCTA.action();
              },
            },
          };

          show(content);
        }
      } catch (err) {
        logException(err);
      }
    },
    [show, markAsVisited, hasVisitedLocally, markAsVisitedLocally]
  );

  const close = useCallback(() => {
    setQueue((prev) => prev.slice(1));
  }, []);

  return (
    <FeatureAnnouncementContext.Provider
      value={{ isOpen, content, showFeatureAnnouncementModal, close }}
    >
      {children}
    </FeatureAnnouncementContext.Provider>
  );
};

const useFeatureAnnouncement = () => {
  const ctx = useContext(FeatureAnnouncementContext);
  if (!ctx) {
    throw new Error(
      "useFeatureAnnouncement must be used within FeatureAnnouncementProvider"
    );
  }
  return ctx;
};

export default useFeatureAnnouncement;
